import type { CSSProperties, FormEvent } from "react";
import { useRef, useState } from "react";
import { Btn } from "@/shared/ui/btn";
import { sendFeedback } from "../api/sendFeedback";

/**
 * Отправляет данные формы обратной связи на тестовый API.
 * @returns {ReactNode}
 */
export const FeedbackForm = () => {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isSent, setIsSent ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const headingStyles = {
    "--heading-font-weight": "700",
    "--heading-line-height": "1.1",
    "--heading-margin-desktop": "1rem",
    "--heading-margin-tablet": "1rem",
    "--heading-margin-mobile": "1rem",
  } as CSSProperties;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formRef.current) {
      return;
    }

    const formData = new FormData(formRef.current);
    setIsLoading(true);
    setError(null);

    try {
      await sendFeedback(formData);
      setIsSent(true);
      formRef.current.reset();
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className={"w-full mx-auto flex mobile:flex-col tablet:gap-4 flex-row items-start justify-between gap-16 !mt-0 mb-40 mobile:mb-12 mobile:items-center mobile:text-center mobile:gap-6"}>
      {/* ===== Левый текст ===== */}
      <div className={"flex-1 flex flex-col gap-8 mobile:gap-4"}>
        <h2 style={headingStyles} className={"text-cherrywood h2"}>
          Let&apos;s make this <br /> better together!
        </h2>
        <p className={"text-cherrywood/80 subtitle"}>
          Have ideas, issues, or feedback? <br /> We&apos;re here to listen.
        </p>
      </div>

      {/* ===== Форма ===== */}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={"flex-1 flex flex-col gap-4 w-full mobile:gap-3"}
      >
        <input
          type={"text"}
          name={"name"}
          placeholder={"Your name"}
          required
          className={"w-full px-6 py-4 bg-cherrywood/30 text-white rounded-[2rem] placeholder-white/70 outline-none focus:ring-2 focus:ring-white/70 transition"}
        />
        <input
          type={"email"}
          name={"email"}
          placeholder={"E-mail"}
          required
          className={"w-full px-6 py-4 bg-cherrywood/30 text-white rounded-[2rem] placeholder-white/70 outline-none focus:ring-2 focus:ring-white/70 transition"}
        />
        <textarea
          name={"message"}
          placeholder={"Comment"}
          rows={4}
          required
          className={"w-full px-6 py-4 bg-cherrywood/30 text-white rounded-[2rem] placeholder-white/70 outline-none focus:ring-2 focus:ring-white/70 transition resize-none"}
        />

        <div className={"mt-4 relative rounded-full shadow-[0_8px_32px_rgba(255,119,162,0.45)] mobile:mt-0"}>
          <Btn
            type={"submit"}
            typeStyle={"secondary"}
            className={"text-cherrywood w-full justify-center"}
            isLoading={isLoading}
            disableRipple
          >
            {isLoading ? "Sending..." : "Contact us"}
          </Btn>
        </div>

        {/* статус */}
        {isSent && !error && (
          <p className={"text-green-600 text-center mt-2 text-cherrywood"}>Feedback sent!</p>
        )}
        {error && (
          <p className={"text-red-500 text-center mt-2 text-cherrywood"}>{error}</p>
        )}
      </form>
    </section>
  );
};
